// src/pages/pengamatan/sensus-populasi-anakan-pisang/SensusPopulasiAnakanPisang.jsx
import React, { useState, useEffect } from "react";
import {
    Box, Card, CardContent,
    CircularProgress, Alert, Snackbar,
    Table, TableContainer, TablePagination
} from "@mui/material";
import { useOutletContext } from "react-router-dom";
import useAuthCheck from "../../../hooks/useAuthCheck";

import CustomBreadcrumbs from '../../../components/universal-features/breadcrumbs';
import SensusPopulasiAnakanPisangTableHeader from './SensusPopulasiAnakanPisangTableHeader';
import SensusPopulasiAnakanPisangTableBody from './SensusPopulasiAnakanPisangTableBody';
import SensusPopulasiAnakanPisangToolbar from './SensusPopulasiAnakanPisangToolbar';
import SensusPopulasiAnakanPisangFilterCard from './SensusPopulasiAnakanPisangFilterCard';
import useSensusPopulasiAnakanPisangData from './hooks/useSensusPopulasiAnakanPisangData';
import useFilterControl from './hooks/useFilterControl';
import './css/SensusPopulasiAnakanPisang.css';

const SensusPopulasiAnakanPisang = () => {
    const { user, loading: authLoading, userProfile } = useAuthCheck();
    const { breadcrumbs } = useOutletContext();

    // Gunakan custom hook untuk mendapatkan data
    const {
        orders,
        loadingOrders,
        snackbar,
        setSnackbar,
        userJabatan,
        userRoleNames,
        updateStatusPengamatan
    } = useSensusPopulasiAnakanPisangData(user, userProfile);
    
    // Gunakan custom hook untuk kontrol filter
    const {
        isFilterOpen,
        statusFilter,
        dateSort,
        toggleFilter,
        handleStatusFilterChange,
        handleDateSortChange,
        resetFilter,
        applyFilter
    } = useFilterControl();
    
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    // State untuk pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // Fungsi untuk mencari data berdasarkan lokasi
    const handleSearch = (term) => {
        setSearchTerm(term);
        if (term === "") {
            setFilteredOrders(orders);
        } else {
            const filtered = orders.filter(order => 
                order.block.toLowerCase().includes(term.toLowerCase())
            );
            setFilteredOrders(filtered);
        }
        setPage(0); // Reset ke halaman pertama saat mencari
    };

    // Fungsi untuk mereset filter pencarian
    const handleResetFilter = () => {
        setSearchTerm("");
        setFilteredOrders(orders);
        setPage(0); // Reset ke halaman pertama saat reset filter
    };

    // Fungsi untuk menerapkan filter
    const handleApplyFilter = () => {
        const filtered = applyFilter(orders);
        setFilteredOrders(filtered);
        setPage(0); // Reset ke halaman pertama saat menerapkan filter
    };

    // Fungsi untuk reset semua filter
    const handleResetAllFilters = () => {
        resetFilter();
        setFilteredOrders(orders);
        setPage(0);
    };

    // Update filteredOrders ketika orders berubah
    useEffect(() => {
        setFilteredOrders(orders);
    }, [orders]);

    // Fungsi untuk handle perubahan halaman
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Fungsi untuk handle perubahan jumlah baris per halaman
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset ke halaman pertama saat mengubah rowsPerPage
    };

    // Data yang ditampilkan berdasarkan pagination
    const paginatedOrders = filteredOrders.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    // Fungsi untuk mengecek apakah semua item pada halaman saat ini terpilih
    const areAllItemsOnCurrentPageSelected = () => {
        if (paginatedOrders.length === 0) return false;
        return paginatedOrders.every(order => selectedItems.includes(order.id));
    };

    // Fungsi untuk handle select all - hanya untuk halaman saat ini
    const handleSelectAll = (event) => {
        if (event.target.checked) {
            // Hanya pilih semua item yang ditampilkan di halaman saat ini
            const currentPageItemIds = paginatedOrders.map(order => order.id);
            // Gabungkan dengan item yang sudah dipilih dari halaman lain (jika ada)
            const newSelectedItems = [...new Set([...selectedItems, ...currentPageItemIds])];
            setSelectedItems(newSelectedItems);
        } else {
            // Hapus hanya item yang ada di halaman saat ini dari selectedItems
            const currentPageItemIds = paginatedOrders.map(order => order.id);
            const newSelectedItems = selectedItems.filter(id => !currentPageItemIds.includes(id));
            setSelectedItems(newSelectedItems);
        }
    };

    useEffect(() => {
        // Hitung tinggi total header (kedua baris)
        const headerRows = document.querySelectorAll('.sensus-populasi-anakan-pisang-table-header');
        let totalHeight = 0;
        
        headerRows.forEach(row => {
            totalHeight += row.offsetHeight;
        });
        
        if (totalHeight > 0) {
            const container = document.querySelector('.sensus-populasi-anakan-pisang-container');
            if (container) {
                container.style.setProperty('--header-height', `${totalHeight}px`);
            }
        }
    }, [filteredOrders]);

    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    const handleSelectItem = (id) => {
        if (selectedItems.includes(id)) {
            setSelectedItems(selectedItems.filter(itemId => itemId !== id));
        } else {
            setSelectedItems([...selectedItems, id]);
        }
    };

    const handleStatusUpdate = (snackbarData) => {
        setSnackbar(snackbarData);
    };

    // Hitung jumlah filter aktif
    const filterCount = (statusFilter ? 1 : 0) + (dateSort !== 'ascending' ? 1 : 0);

    if (authLoading || loadingOrders) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    Anda harus login untuk mengakses halaman ini.
                </Alert>
            </Box>
        );
    }

    // Fungsi untuk reload halaman
    const handleReload = () => {
        setTimeout(() => {
            window.location.reload();
        }, 1000);
    };

    return (
        <Box className="sensus-populasi-anakan-pisang-container">
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            {/* Filter Card */}
            <SensusPopulasiAnakanPisangFilterCard
                isFilterOpen={isFilterOpen}
                statusFilter={statusFilter}
                dateSort={dateSort}
                onStatusFilterChange={handleStatusFilterChange}
                onDateSortChange={handleDateSortChange}
                onResetFilter={handleResetAllFilters}
                onApplyFilter={handleApplyFilter}
            />

            <Card>
                <CardContent sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                    <SensusPopulasiAnakanPisangToolbar
                        searchTerm={searchTerm}
                        onSearchChange={(e) => handleSearch(e.target.value)}
                        onResetFilter={handleResetFilter}
                        anchorEl={anchorEl}
                        onMenuClick={handleClick}
                        onMenuClose={handleClose}
                        open={open}
                        selectedItems={selectedItems}
                        userJabatan={userJabatan}
                        userRoleNames={userRoleNames}
                        updateStatusPengamatan={updateStatusPengamatan}
                        onStatusUpdate={handleStatusUpdate}
                        onReload={handleReload}
                        orders={orders}
                        // Props baru untuk filter control
                        isFilterOpen={isFilterOpen}
                        onToggleFilter={toggleFilter}
                        onApplyFilter={handleApplyFilter}
                        filterCount={filterCount}
                    />
                    
                    <TableContainer className="sensus-populasi-anakan-pisang-table-container">
                        <Table stickyHeader className="sensus-populasi-anakan-pisang-table">
                            <SensusPopulasiAnakanPisangTableHeader 
                                selectedItems={selectedItems}
                                orders={paginatedOrders}
                                handleSelectAll={handleSelectAll}
                                areAllItemsOnCurrentPageSelected={areAllItemsOnCurrentPageSelected}
                            />
                            <SensusPopulasiAnakanPisangTableBody 
                                orders={paginatedOrders}
                                selectedItems={selectedItems}
                                handleSelectItem={handleSelectItem}
                                loadingOrders={loadingOrders}
                            />
                        </Table>
                    </TableContainer>

                    {/* TablePagination */}
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={filteredOrders.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        classes={{
                            select: 'TablePagination-select',
                            displayedRows: 'TablePagination-displayedRows',
                            actions: 'TablePaginationActions-root'
                        }}
                        labelRowsPerPage="Baris per halaman:"
                        labelDisplayedRows={({ from, to, count }) => 
                            `${from}-${to} dari ${count !== -1 ? count : `lebih dari ${to}`}`
                        }
                    />
                </CardContent>
            </Card>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SensusPopulasiAnakanPisang;